import { Injectable, NgModuleRef } from '@angular/core';
import { ConfigurationType } from '@icc/shared/types';
import { MutableConfiguratorContext, createConfiguratorContext } from './configurator-context';
import { ConfiguratorServiceRegistry } from './+state/configurator-service-registry';

/**
 * Represents a configurator instance with its moduleRef and context
 */
export interface ConfiguratorInstance {
    readonly instanceId: string;
    readonly type: ConfigurationType;
    readonly configurationId: string | null;
    readonly moduleRef: NgModuleRef<any>;
    readonly context: MutableConfiguratorContext;
    readonly createdAt: number;
}

/**
 * Registry for managing active configurator instances.
 *
 * This service tracks all active configurator instances and their associated
 * module references, enabling proper cleanup when instances are destroyed.
 */
@Injectable({
    providedIn: 'root'
})
export class ConfiguratorInstanceRegistry {
    private instances = new Map<string, ConfiguratorInstance>();
    private activeInstanceId: string | null = null;

    constructor(
        private serviceRegistry: ConfiguratorServiceRegistry
    ) {}

    /**
     * Register a new configurator instance
     */
    register(
        type: ConfigurationType,
        configurationId: string | null,
        moduleRef: NgModuleRef<any>,
        context?: MutableConfiguratorContext
    ): ConfiguratorInstance {
        // Use provided context or create a new one
        const instanceContext = context ?? createConfiguratorContext(type, configurationId);

        const instance: ConfiguratorInstance = {
            instanceId: instanceContext.instanceId,
            type,
            configurationId,
            moduleRef,
            context: instanceContext,
            createdAt: Date.now(),
        };

        this.instances.set(instanceContext.instanceId, instance);
        this.activeInstanceId = instanceContext.instanceId;

        return instance;
    }

    /**
     * Get the currently active instance
     */
    getActiveInstance(): ConfiguratorInstance | null {
        if (!this.activeInstanceId) {
            return null;
        }
        return this.instances.get(this.activeInstanceId) || null;
    }

    /**
     * Get the active instance ID
     */
    getActiveInstanceId(): string | null {
        return this.activeInstanceId;
    }

    /**
     * Set the active instance by ID
     */
    setActiveInstance(instanceId: string): void {
        if (!this.instances.has(instanceId)) {
            throw new Error(`Instance ${instanceId} not found in registry`);
        }
        this.activeInstanceId = instanceId;
    }

    /**
     * Get an instance by ID
     */
    getInstance(instanceId: string): ConfiguratorInstance | null {
        return this.instances.get(instanceId) || null;
    }

    /**
     * Get all instances of a specific type
     */
    getInstancesByType(type: ConfigurationType): ConfiguratorInstance[] {
        return Array.from(this.instances.values()).filter(i => i.type === type);
    }

    /**
     * Get all registered instances
     */
    getAllInstances(): ConfiguratorInstance[] {
        return Array.from(this.instances.values());
    }

    /**
     * Increment the sequence number of an instance
     * Called when a new configuration is loaded into an existing instance
     */
    incrementSequence(instanceId: string): number {
        const instance = this.instances.get(instanceId);
        if (!instance) {
            throw new Error(`Instance ${instanceId} not found in registry`);
        }
        return instance.context.incrementSequence();
    }

    /**
     * Destroy an instance and clean up resources
     */
    destroy(instanceId: string): void {
        const instance = this.instances.get(instanceId);
        if (!instance) {
            return;
        }

        // Synchronously tear down service subscriptions for THIS instance
        // BEFORE moduleRef.destroy() triggers NgRx store removal.
        // This prevents undefined state emissions from reaching active subscribers.
        // Only services belonging to this instance are torn down; legacy services
        // (created by loadConfigurator without context) are left intact.
        this.serviceRegistry.destroyServicesByInstance(instanceId);

        // Trigger context destroy (for any remaining listeners)
        instance.context.destroy();

        // Now safe to destroy the module ref — no active subscriptions
        instance.moduleRef.destroy();

        // Remove from registry
        this.instances.delete(instanceId);

        // Clear active if this was the active instance
        if (this.activeInstanceId === instanceId) {
            this.activeInstanceId = null;
        }
    }

    /**
     * Destroy all instances of a specific type
     */
    destroyByType(type: ConfigurationType): void {
        const instances = this.getInstancesByType(type);
        instances.forEach(instance => this.destroy(instance.instanceId));
    }

    /**
     * Destroy all instances
     */
    destroyAll(): void {
        const instanceIds = Array.from(this.instances.keys());
        instanceIds.forEach(id => this.destroy(id));
    }

    /**
     * Check if an instance exists
     */
    has(instanceId: string): boolean {
        return this.instances.has(instanceId);
    }

    /**
     * Get the count of active instances
     */
    get count(): number {
        return this.instances.size;
    }
}
